/**
 * CommunitySkeletons — layout-matched loading placeholders for `/community`.
 *
 * The route is both capability-gated (an async `beforeLoad`) and lazily chunked,
 * and TanStack keeps the PREVIOUS route on screen for the whole pending window.
 * Before this existed, leaving a post for the feed froze the old page with no
 * signal that anything was happening.
 *
 * Used as BOTH the route `pendingComponent` (covers `beforeLoad`) and the
 * Suspense fallback (covers the chunk download), so a cold navigation is one
 * continuous structural skeleton rather than frozen page → spinner → content.
 * Mirrors the Feed tab specifically: it is the default tab and the one every
 * "Back to feed" path lands on.
 *
 * Follows the same conventions as EventsSkeletons: the shared <Skeleton>
 * primitive, real container widths, and no controls that only some users see
 * (no settings gear, no admin actions) so nothing vanishes on mount.
 */

import { Skeleton } from '@/components/ui/skeleton';
import { PillBarShell } from '@/components/shared/PillBarShell';

// Staggered widths so the channel list reads as names, not bars.
const CHANNEL_WIDTHS = ['w-24', 'w-20', 'w-28', 'w-16', 'w-24', 'w-20'] as const;

/**
 * ChannelsSidebar shell — 220px matches the real sidebar's expanded width
 * (its motion.aside animates to exactly 220).
 */
function ChannelsSidebarShell() {
  return (
    <aside className="hidden w-[220px] flex-shrink-0 flex-col overflow-hidden bg-card md:flex">
      {/* Community selector pill */}
      <div className="p-3">
        <Skeleton className="h-[38px] w-full rounded-lg" />
      </div>
      {/* CHANNELS group */}
      <div className="space-y-1 px-3">
        <Skeleton className="mb-2 h-2.5 w-16" />
        {CHANNEL_WIDTHS.map((w, i) => (
          <div key={i} className="flex items-center gap-2 px-1 py-1.5">
            <Skeleton className="h-5 w-5 flex-shrink-0 rounded-md" />
            <Skeleton className={`h-3 ${w}`} />
          </div>
        ))}
      </div>
      {/* GLOBAL group */}
      <div className="space-y-1 px-3 pt-4">
        <Skeleton className="mb-2 h-2.5 w-12" />
        <div className="flex items-center gap-2 px-1 py-1.5">
          <Skeleton className="h-5 w-5 flex-shrink-0 rounded-md" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
    </aside>
  );
}

/**
 * Feed header — tier avatar + name/member count. Search/settings omitted.
 *
 * The name/members block is built from explicit line boxes (29px + 16px) rather
 * than a `space-y-*` stack: that's what the real `<h3>` + `<p class="text-xs">`
 * measure, and it makes the header exactly 82px so nothing shifts on mount.
 */
function FeedHeaderShell() {
  return (
    <div className="flex-shrink-0 border-b border-border px-4 pb-3">
      <div className="mb-3 flex items-start gap-3 pt-3">
        <Skeleton className="h-10 w-10 flex-shrink-0 rounded-xl" />
        <div>
          <div className="flex h-[29px] items-center">
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="flex h-4 items-center">
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Stories strip — circles + captions. 64px circle + `gap-1` + a 16px caption
 * line box is exactly the real item's 84px.
 */
function StoriesStripShell() {
  return (
    <div className="flex-shrink-0 border-b border-border bg-card py-3">
      <div className="flex gap-3 px-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex flex-col items-center gap-1">
            <Skeleton className="h-16 w-16 rounded-full" />
            <div className="flex h-4 items-center">
              <Skeleton className="h-2.5 w-12" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Feed column — composer + post cards inside the same `max-w-2xl` container the
 * real feed uses, so posts settle into place without a horizontal jump.
 */
function FeedColumnShell() {
  return (
    <div className="flex-1 min-h-0 overflow-hidden">
      <div className="mx-auto w-full max-w-2xl" aria-busy="true" aria-label="Loading feed">
        {/* Composer — 44px avatar beside a 50px input is the real 83px block. */}
        <div className="border-b border-border bg-card py-4 pl-3 pr-5 sm:p-4">
          <div className="flex items-start gap-3">
            <Skeleton className="h-11 w-11 flex-shrink-0 rounded-full" />
            <Skeleton className="h-[50px] flex-1 rounded-2xl" />
          </div>
        </div>
        {/* Posts — the real list butts straight up against the composer. */}
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-sm border border-border bg-card shadow-sm">
              {/* Author row — w-11 avatar matches Avatar size="md" */}
              <div className="flex items-center gap-3 px-4 pb-2 pt-4">
                <Skeleton className="h-11 w-11 flex-shrink-0 rounded-full" />
                <div className="space-y-1.5">
                  <Skeleton className="h-3.5 w-28" />
                  <Skeleton className="h-2.5 w-16" />
                </div>
              </div>
              {/* Body — the one genuinely variable block; two lines reads as a
                  short post without pretending to know the real length. */}
              <div className="space-y-2 px-4 pb-2">
                <Skeleton className="h-3.5 w-full" />
                <Skeleton className="h-3.5 w-4/5" />
              </div>
              {/* Like / Comment / Share row */}
              <div className="flex items-center justify-between border-t border-border px-4 py-1">
                <Skeleton className="h-9 w-16 rounded-lg" />
                <Skeleton className="h-9 w-24 rounded-lg" />
                <Skeleton className="h-9 w-20 rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Full-page feed skeleton — chrome shell + sidebar + feed column, matching the
 * outer structure of pages/Community.tsx on the Feed tab. Used as BOTH the
 * `/community` route `pendingComponent` and its Suspense fallback.
 */
export function CommunityFeedSkeleton() {
  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden bg-background">
      <PillBarShell />
      <div className="flex-1 min-h-0 flex overflow-hidden">
        <ChannelsSidebarShell />
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden bg-background">
          <FeedHeaderShell />
          <StoriesStripShell />
          <FeedColumnShell />
        </div>
      </div>
    </div>
  );
}
